import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { useConfirmationModal } from "../app/confirmation";
import { useAuth } from "./auth";
import { TrainingModel } from "../../api/training/TrainingModel";
import { TrainingAdapter } from "../../api/training/TrainingAdapter";
import { extractDataFromResponse } from "../../utils/paginationHelpers";
import type {
  TrainingTemplate,
  Training,
  TrainingCourse,
  Session,
  CreateTrainingTemplatePayload,
  TrainingsPaginationRequest,
  TrainingSearchRequest,
  TrainingAlertGroup,
  TrainingTemplatesPaginationRequest,
  TrainingTemplateExternalOptionsResponse,
  ExternalProviderType,
} from "../../api/training/Training.typed";
import type { CourseTemplate } from "../../api/followup/Followup.typed";
import type { TheoryTestTemplate } from "../../api/theoryTests/TheoryTest.typed";
import type { TrainingMailEvent } from "../../api/mailTemplates/MailTemplate.typed";
import type { PaginatedResponse } from "../../api/zekay/pagination";

/**
 * Status mutation helpers: `skipConfirmation` bypasses the built-in dialog;
 * `rethrowOnError` / `suppressSuccessAlert` support batched callers (e.g. excursion validation).
 */
export type TrainingStatusMutationOptions = {
  skipConfirmation?: boolean;
  rethrowOnError?: boolean;
  suppressSuccessAlert?: boolean;
};

export type TrainingUpdateOptions = {
  rethrowOnError?: boolean;
  suppressSuccessAlert?: boolean;
};

interface TrainingContextType {
  qualifications: TrainingTemplate[] | undefined;
  refreshTrainingTemplates: () => Promise<void>;
  getTrainingsTemplates: (
    paginationParams?: TrainingTemplatesPaginationRequest,
  ) => Promise<PaginatedResponse<TrainingTemplate> | TrainingTemplate[] | undefined>;
  /** True when the template was saved (false on cancel or error). */
  updateTrainingTemplate: (form: TrainingTemplate) => Promise<boolean>;
  /** True when the template was deleted (false on cancel or error). */
  deleteTrainingTemplate: (id: number) => Promise<boolean>;
  createTrainingTemplate: (
    payload: CreateTrainingTemplatePayload,
  ) => Promise<TrainingTemplate | void>;
  getTrainingTemplatesExternalOptions: (
    type: ExternalProviderType,
  ) => Promise<TrainingTemplateExternalOptionsResponse | undefined>;
  getDoneTrainings: (
    paginationParams?: TrainingsPaginationRequest,
  ) => Promise<PaginatedResponse<Training> | Training[] | undefined>;
  getTrainingAlerts: () => Promise<TrainingAlertGroup[] | undefined>;
  createTraining: (form: Training) => Promise<void>;
  deleteTraining: (form: Training) => Promise<void>;
  cancelTraining: (form: Training, options?: TrainingStatusMutationOptions) => Promise<void>;
  updateTraining: (form: Training, options?: TrainingUpdateOptions) => Promise<void>;
  /**
   * Bumps the `Sessions` counter (+1) of every given training in a single
   * server round-trip. Silent on success: no toast is shown so it can be
   * chained after another action (typically the excursion validation modal
   * decrementing product counters in batch).
   */
  batchIncrementTrainingSessions: (trainingIds: number[]) => Promise<number | undefined>;
  /** Resolves to false when the mail was not sent (refusal, missing template or API error). */
  sendTrainingMail: (trainingId: number, event: TrainingMailEvent) => Promise<boolean>;
  validateUserQualification: (
    form: Training,
    options?: TrainingStatusMutationOptions,
  ) => Promise<void>;
  updateTrainingFile: (trainingId: number, formData: FormData) => Promise<void>;
  deleteTrainingFile: (trainingId: number, type: string) => Promise<void>;
  getTrainingsByTemplateId: (templateId: string) => Promise<Training[] | undefined>;
  getTrainingsByTemplateIdAndStatus: (
    templateId: string,
    status: number,
    paginationParams?: TrainingsPaginationRequest,
  ) => Promise<PaginatedResponse<Training> | Training[] | undefined>;
  searchTrainings: (
    searchRequest: TrainingSearchRequest,
  ) => Promise<PaginatedResponse<Training> | Training[] | undefined>;
  getTrainingByUserTemplateId: (
    templateId: number,
    userId: number,
  ) => Promise<Training | undefined>;
  getCourseTemplate: (id: number) => Promise<CourseTemplate | undefined>;
  createCourseTemplate: (ct: CourseTemplate) => Promise<void>;
  updateCourseTemplate: (ct: CourseTemplate) => Promise<void>;
  createSession: (form: Session) => Promise<void>;
  updateSession: (session: Session) => Promise<void>;
  generateSessionPdf: (templateId: number, customerId: number) => Promise<void>;
  getTheoryTestTemplate: (templateId: number) => Promise<TheoryTestTemplate | undefined>;
  updateTheoryTestTemplate: (template: TheoryTestTemplate) => Promise<void>;
  createTheoryTestTemplate: (template: TheoryTestTemplate) => Promise<void>;
  /** True when the training template has a published (online) theory test. */
  isTheoryTestOnline: (templateId?: number) => boolean;
  deleteSession: (templateId: number, customerId: number, number: number) => Promise<void>;
  listTrainingCourses: (templateId: number) => Promise<TrainingCourse[] | undefined>;
  addTrainingCourse: (
    templateId: number,
    file: Blob,
    name?: string,
  ) => Promise<TrainingCourse | undefined>;
  deleteTrainingCourse: (templateId: number, courseId: string) => Promise<void>;
  deleteTrainingCourseSilent: (templateId: number, courseId: string) => Promise<void>;
  replaceTrainingCourses: (templateId: number, courses: TrainingCourse[]) => Promise<void>;
  uploadTrainingTemplateResource: (
    templateId: number,
    field: "course1" | "course2" | "theory" | "agreement" | "disclaimer",
    file: Blob,
  ) => Promise<TrainingTemplate | undefined>;
}

const TrainingContext = React.createContext<TrainingContextType>(null!);

export function TrainingProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();
  const { user } = useAuth();
  const tenantCompanyId = user?.Role?.company_id ?? user?.company_id;

  const [qualifications, setQualifications] = React.useState<TrainingTemplate[] | undefined>(
    undefined,
  );
  const [onlineTheoryTestTemplateIds, setOnlineTheoryTestTemplateIds] = React.useState<
    Set<number> | undefined
  >(undefined);

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new TrainingModel(appConfig);
    return new TrainingAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const refreshTrainingTemplates = React.useCallback(async () => {
    if (!adapter) return;
    try {
      const templates = await adapter.getTrainingTemplates();
      const data = extractDataFromResponse(templates);
      setQualifications(data);
    } catch (err) {
      if (err instanceof Error) showAlert(err.message, "danger");
    }
  }, [adapter, showAlert]);

  const getTrainingsTemplates = React.useCallback(
    async (
      paginationParams?: TrainingTemplatesPaginationRequest,
    ): Promise<PaginatedResponse<TrainingTemplate> | TrainingTemplate[] | undefined> =>
      adapter ? wrap(() => adapter.getTrainingTemplates(paginationParams)) : undefined,
    [adapter, wrap],
  );

  const updateTrainingTemplate = React.useCallback(
    async (form: TrainingTemplate): Promise<boolean> => {
      if (!adapter) return false;
      const ok = await showConfirmation(
        "Modifier le template",
        "Êtes-vous sûr de vouloir enregistrer les modifications ?",
      );
      if (!ok) return false;
      try {
        await adapter.updateTrainingTemplate(form);
        showAlert("Template mis à jour", "success");
        await refreshTrainingTemplates();
        return true;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return false;
      }
    },
    [adapter, showAlert, showConfirmation, refreshTrainingTemplates],
  );

  const deleteTrainingTemplate = React.useCallback(
    async (id: number): Promise<boolean> => {
      if (!adapter) return false;
      const ok = await showConfirmation(
        "Supprimer le template",
        "Êtes-vous sûr de vouloir supprimer ce template ?",
      );
      if (!ok) return false;
      try {
        await adapter.deleteTrainingTemplate(id);
        showAlert("Template supprimé", "success");
        await refreshTrainingTemplates();
        return true;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return false;
      }
    },
    [adapter, showAlert, showConfirmation, refreshTrainingTemplates],
  );

  const createTrainingTemplate = React.useCallback(
    async (payload: CreateTrainingTemplatePayload): Promise<TrainingTemplate | void> => {
      if (!adapter) return;
      const r = await wrap(() => adapter.createTrainingTemplate(payload));
      if (r !== undefined) {
        showAlert("Template créé", "success");
        await refreshTrainingTemplates();
        return r;
      }
    },
    [adapter, wrap, showAlert, refreshTrainingTemplates],
  );

  const getTrainingTemplatesExternalOptions = React.useCallback(
    async (type: ExternalProviderType) =>
      adapter ? wrap(() => adapter.getTrainingTemplatesExternalOptions(type)) : undefined,
    [adapter, wrap],
  );

  const getDoneTrainings = React.useCallback(
    async (
      paginationParams?: TrainingsPaginationRequest,
    ): Promise<PaginatedResponse<Training> | Training[] | undefined> =>
      adapter ? wrap(() => adapter.getDoneTrainings(paginationParams)) : undefined,
    [adapter, wrap],
  );

  const getTrainingAlerts = React.useCallback(
    async (): Promise<TrainingAlertGroup[] | undefined> =>
      adapter ? wrap(() => adapter.getTrainingAlerts()) : undefined,
    [adapter, wrap],
  );

  const createTraining = React.useCallback(
    async (form: Training): Promise<void> => {
      if (!adapter) return;
      try {
        const { UpdatedAt: _updatedAt, ...payload } = form;
        await adapter.createTraining(payload);
        showAlert("Formation créée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const deleteTraining = React.useCallback(
    async (form: Training): Promise<void> => {
      if (!adapter) return;
      const ok = await showConfirmation(
        "Supprimer la formation",
        "Êtes-vous sûr de vouloir supprimer cette formation ?",
      );
      if (!ok) return;
      try {
        await adapter.deleteTraining(form);
        showAlert("Formation supprimée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const cancelTraining = React.useCallback(
    async (form: Training, options?: TrainingStatusMutationOptions): Promise<void> => {
      if (!adapter) return;
      if (!options?.skipConfirmation) {
        const ok = await showConfirmation(
          "Annuler la formation",
          "Confirmer l'annulation de cette formation ?",
        );
        if (!ok) return;
      }
      try {
        const { UpdatedAt: _updatedAt, ...payload } = form;
        await adapter.updateTraining({ ...payload, Status: "Annulé" });
        if (!options?.suppressSuccessAlert) {
          showAlert("Formation annulée", "success");
        }
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        if (options?.rethrowOnError) {
          throw err;
        }
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const updateTraining = React.useCallback(
    async (form: Training, options?: TrainingUpdateOptions): Promise<void> => {
      if (!adapter) return;
      try {
        const { UpdatedAt: _updatedAt, ...payload } = form;
        await adapter.updateTraining(payload);
        if (!options?.suppressSuccessAlert) {
          showAlert("Formation mise à jour", "success");
        }
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        if (options?.rethrowOnError) {
          throw err;
        }
      }
    },
    [adapter, showAlert],
  );

  const batchIncrementTrainingSessions = React.useCallback(
    async (trainingIds: number[]): Promise<number | undefined> => {
      if (!adapter) return undefined;
      if (trainingIds.length === 0) return 0;
      try {
        return await adapter.batchIncrementTrainingSessions(trainingIds);
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [adapter, showAlert],
  );

  const sendTrainingMail = React.useCallback(
    async (trainingId: number, event: TrainingMailEvent): Promise<boolean> => {
      if (!adapter) return false;
      const ok = await showConfirmation(
        "Envoyer l'e-mail",
        "Êtes-vous sûr de vouloir envoyer cet e-mail ?",
      );
      if (!ok) return false;
      try {
        await adapter.sendTrainingMail(trainingId, event);
        showAlert("E-mail envoyé", "success");
        return true;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return false;
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const validateUserQualification = React.useCallback(
    async (form: Training, options?: TrainingStatusMutationOptions): Promise<void> => {
      if (!adapter) return;
      if (!options?.skipConfirmation) {
        const ok = await showConfirmation(
          "Valider la qualification",
          "Êtes-vous sûr de vouloir valider cette qualification ?",
        );
        if (!ok) return;
      }
      try {
        const { UpdatedAt: _updatedAt, ...payload } = form;
        await adapter.validateUserQualification(payload);
        showAlert("Qualification validée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const updateTrainingFile = React.useCallback(
    async (trainingId: number, formData: FormData): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateTrainingFile(trainingId, formData);
        showAlert("Fichier mis à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const deleteTrainingFile = React.useCallback(
    async (trainingId: number, type: string): Promise<void> => {
      if (!adapter) return;
      const ok = await showConfirmation(
        "Supprimer le fichier",
        "Êtes-vous sûr de vouloir supprimer ce fichier ?",
      );
      if (!ok) return;
      try {
        await adapter.deleteTrainingFile(trainingId, type);
        showAlert("Fichier supprimé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const getTrainingsByTemplateId = React.useCallback(
    async (templateId: string): Promise<Training[] | undefined> =>
      adapter ? wrap(() => adapter.getTrainingsByTemplateId(templateId)) : undefined,
    [adapter, wrap],
  );

  const getTrainingsByTemplateIdAndStatus = React.useCallback(
    async (
      templateId: string,
      status: number,
      paginationParams?: TrainingsPaginationRequest,
    ): Promise<PaginatedResponse<Training> | Training[] | undefined> =>
      adapter
        ? wrap(() =>
            adapter.getTrainingsByTemplateIdAndStatus(templateId, status, paginationParams),
          )
        : undefined,
    [adapter, wrap],
  );

  const searchTrainings = React.useCallback(
    async (
      searchRequest: TrainingSearchRequest,
    ): Promise<PaginatedResponse<Training> | Training[] | undefined> =>
      adapter ? wrap(() => adapter.searchTrainings(searchRequest)) : undefined,
    [adapter, wrap],
  );

  const getTrainingByUserTemplateId = React.useCallback(
    async (templateId: number, userId: number): Promise<Training | undefined> =>
      adapter ? wrap(() => adapter.getTrainingByUserTemplateId(templateId, userId)) : undefined,
    [adapter, wrap],
  );

  const getCourseTemplate = React.useCallback(
    async (id: number): Promise<CourseTemplate | undefined> =>
      adapter ? wrap(() => adapter.getCourseTemplate(id)) : undefined,
    [adapter, wrap],
  );

  const createCourseTemplate = React.useCallback(
    async (ct: CourseTemplate): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.createCourseTemplate(ct);
        showAlert("Template de cours créé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const updateCourseTemplate = React.useCallback(
    async (ct: CourseTemplate): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateCourseTemplate(ct);
        showAlert("Template de cours mis à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const createSession = React.useCallback(
    async (form: Session): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.createSession(form);
        showAlert("Session créée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const updateSession = React.useCallback(
    async (session: Session): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateSession(session);
        showAlert("Session mise à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const generateSessionPdf = React.useCallback(
    async (templateId: number, customerId: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.generateSessionPDF(templateId, customerId);
        showAlert("PDF généré", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const getTheoryTestTemplate = React.useCallback(
    async (templateId: number): Promise<TheoryTestTemplate | undefined> =>
      adapter ? wrap(() => adapter.getTheoryTestTemplate(templateId)) : undefined,
    [adapter, wrap],
  );

  const refreshOnlineTheoryTestTemplateIds = React.useCallback(async () => {
    if (!adapter) return;
    try {
      const ids = await adapter.listOnlineTheoryTestTemplateIds();
      setOnlineTheoryTestTemplateIds(new Set(ids));
    } catch {
      setOnlineTheoryTestTemplateIds(new Set<number>());
    }
  }, [adapter]);

  // The tenant header is derived from the session user, so loading earlier would 400.
  React.useEffect(() => {
    if (!adapter || tenantCompanyId == null) {
      setOnlineTheoryTestTemplateIds(undefined);
      return;
    }
    void refreshOnlineTheoryTestTemplateIds();
  }, [adapter, tenantCompanyId, refreshOnlineTheoryTestTemplateIds]);

  const isTheoryTestOnline = React.useCallback(
    (templateId?: number): boolean =>
      templateId != null && (onlineTheoryTestTemplateIds?.has(templateId) ?? false),
    [onlineTheoryTestTemplateIds],
  );

  const updateTheoryTestTemplate = React.useCallback(
    async (template: TheoryTestTemplate): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateTheoryTestTemplate(template);
        showAlert("Template de test mis à jour", "success");
        await refreshOnlineTheoryTestTemplateIds();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, refreshOnlineTheoryTestTemplateIds],
  );

  const createTheoryTestTemplate = React.useCallback(
    async (template: TheoryTestTemplate): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.createTheoryTestTemplate(template);
        showAlert("Template de test créé", "success");
        await refreshOnlineTheoryTestTemplateIds();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, refreshOnlineTheoryTestTemplateIds],
  );

  const deleteSession = React.useCallback(
    async (templateId: number, customerId: number, number: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.deleteSession(templateId, customerId, number);
        showAlert("Session supprimée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const listTrainingCourses = React.useCallback(
    async (templateId: number): Promise<TrainingCourse[] | undefined> =>
      adapter ? wrap(() => adapter.listTrainingCourses(templateId)) : undefined,
    [adapter, wrap],
  );

  const addTrainingCourse = React.useCallback(
    async (templateId: number, file: Blob, name?: string): Promise<TrainingCourse | undefined> =>
      adapter ? wrap(() => adapter.addTrainingCourse(templateId, file, name)) : undefined,
    [adapter, wrap],
  );

  const deleteTrainingCourse = React.useCallback(
    async (templateId: number, courseId: string): Promise<void> => {
      if (!adapter) return;
      const ok = await showConfirmation(
        "Supprimer le cours",
        "Êtes-vous sûr de vouloir supprimer ce cours ?",
      );
      if (!ok) return;
      try {
        await adapter.deleteTrainingCourse(templateId, courseId);
        showAlert("Cours supprimé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const deleteTrainingCourseSilent = React.useCallback(
    async (templateId: number, courseId: string): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.deleteTrainingCourse(templateId, courseId);
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const replaceTrainingCourses = React.useCallback(
    async (templateId: number, courses: TrainingCourse[]): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.replaceTrainingCourses(templateId, courses);
        showAlert("Cours remplacés", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const uploadTrainingTemplateResource = React.useCallback(
    async (
      templateId: number,
      field: "course1" | "course2" | "theory" | "agreement" | "disclaimer",
      file: Blob,
    ): Promise<TrainingTemplate | undefined> =>
      adapter
        ? wrap(() => adapter.uploadTrainingTemplateResource(templateId, field, file))
        : undefined,
    [adapter, wrap],
  );

  const value = React.useMemo<TrainingContextType>(
    () => ({
      qualifications,
      refreshTrainingTemplates,
      getTrainingsTemplates,
      updateTrainingTemplate,
      deleteTrainingTemplate,
      createTrainingTemplate,
      getTrainingTemplatesExternalOptions,
      getDoneTrainings,
      getTrainingAlerts,
      createTraining,
      deleteTraining,
      cancelTraining,
      updateTraining,
      batchIncrementTrainingSessions,
      sendTrainingMail,
      validateUserQualification,
      updateTrainingFile,
      deleteTrainingFile,
      getTrainingsByTemplateId,
      getTrainingsByTemplateIdAndStatus,
      searchTrainings,
      getTrainingByUserTemplateId,
      getCourseTemplate,
      createCourseTemplate,
      updateCourseTemplate,
      createSession,
      updateSession,
      generateSessionPdf,
      getTheoryTestTemplate,
      updateTheoryTestTemplate,
      createTheoryTestTemplate,
      isTheoryTestOnline,
      deleteSession,
      listTrainingCourses,
      addTrainingCourse,
      deleteTrainingCourse,
      deleteTrainingCourseSilent,
      replaceTrainingCourses,
      uploadTrainingTemplateResource,
    }),
    [
      qualifications,
      refreshTrainingTemplates,
      getTrainingsTemplates,
      updateTrainingTemplate,
      deleteTrainingTemplate,
      createTrainingTemplate,
      getTrainingTemplatesExternalOptions,
      getDoneTrainings,
      getTrainingAlerts,
      createTraining,
      deleteTraining,
      cancelTraining,
      updateTraining,
      batchIncrementTrainingSessions,
      sendTrainingMail,
      validateUserQualification,
      updateTrainingFile,
      deleteTrainingFile,
      getTrainingsByTemplateId,
      getTrainingsByTemplateIdAndStatus,
      searchTrainings,
      getTrainingByUserTemplateId,
      getCourseTemplate,
      createCourseTemplate,
      updateCourseTemplate,
      createSession,
      updateSession,
      generateSessionPdf,
      getTheoryTestTemplate,
      updateTheoryTestTemplate,
      createTheoryTestTemplate,
      isTheoryTestOnline,
      deleteSession,
      listTrainingCourses,
      addTrainingCourse,
      deleteTrainingCourse,
      deleteTrainingCourseSilent,
      replaceTrainingCourses,
      uploadTrainingTemplateResource,
    ],
  );

  return <TrainingContext.Provider value={value}>{props.children}</TrainingContext.Provider>;
}

export function useTraining(): TrainingContextType {
  const ctx = React.useContext(TrainingContext);
  if (!ctx) throw new Error("useTraining must be used within TrainingProvider");
  return ctx;
}
